const EDGE_TOLERANCE = 2;
const FOCUS_GUTTER = 8;

export function createCategoryChildNav(root, browserWindow = window) {
    const rail = root.querySelector('[data-category-child-rail]');
    const previous = root.querySelector('[data-category-child-previous]');
    const next = root.querySelector('[data-category-child-next]');

    if (!rail || !previous || !next || root.dataset.categoryChildNavReady === 'true') {
        return null;
    }

    root.dataset.categoryChildNavReady = 'true';

    const prefersReducedMotion = () => browserWindow.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

    const syncState = () => {
        const maximumScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const hasOverflow = maximumScrollLeft > EDGE_TOLERANCE;

        root.dataset.overflow = hasOverflow ? 'true' : 'false';
        root.classList.toggle('category-child-nav--overflowing', hasOverflow);
        previous.hidden = !hasOverflow;
        next.hidden = !hasOverflow;
        previous.disabled = !hasOverflow || rail.scrollLeft <= EDGE_TOLERANCE;
        next.disabled = !hasOverflow || rail.scrollLeft >= maximumScrollLeft - EDGE_TOLERANCE;
    };

    const scrollByViewport = (direction) => {
        rail.scrollBy({
            left: direction * Math.max(160, rail.clientWidth * 0.8),
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
    };

    const keepFocusedLinkVisible = (event) => {
        const link = event.target.closest?.('a[href]');

        if (!link || !rail.contains(link)) {
            return;
        }

        const railBounds = rail.getBoundingClientRect();
        const linkBounds = link.getBoundingClientRect();
        let offset = 0;

        if (linkBounds.left < railBounds.left) {
            offset = linkBounds.left - railBounds.left - FOCUS_GUTTER;
        } else if (linkBounds.right > railBounds.right) {
            offset = linkBounds.right - railBounds.right + FOCUS_GUTTER;
        }

        if (offset !== 0) {
            rail.scrollBy({ left: offset, behavior: 'auto' });
        }
    };

    const scrollPrevious = () => scrollByViewport(-1);
    const scrollNext = () => scrollByViewport(1);

    previous.addEventListener('click', scrollPrevious);
    next.addEventListener('click', scrollNext);
    rail.addEventListener('scroll', syncState, { passive: true });
    rail.addEventListener('focusin', keepFocusedLinkVisible);
    browserWindow.addEventListener('resize', syncState);

    const resizeObserver = browserWindow.ResizeObserver ? new browserWindow.ResizeObserver(syncState) : null;

    resizeObserver?.observe(rail);
    syncState();

    return {
        syncState,
        destroy() {
            previous.removeEventListener('click', scrollPrevious);
            next.removeEventListener('click', scrollNext);
            rail.removeEventListener('scroll', syncState);
            rail.removeEventListener('focusin', keepFocusedLinkVisible);
            browserWindow.removeEventListener('resize', syncState);
            resizeObserver?.disconnect();
            delete root.dataset.categoryChildNavReady;
        },
    };
}

export function initCategoryChildNav(doc = document, browserWindow = window) {
    return Array.from(doc.querySelectorAll('[data-category-child-nav]'))
        .map((root) => createCategoryChildNav(root, browserWindow))
        .filter(Boolean);
}
